import React from 'react';
import { BarChart, Bar, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts';
import { getXAxisLabel, getYAxisLabel } from './GraphLabels';
import { itemSorterFn } from '../../../utils/chartUtils';

const LEGEND_ORDER = {
  Transparent: 1,
  DeltaIncrease: 2,
  DeltaDecrease: 3
};

/**
 * @typedef {object} WaterfallChartProps
 * @property {number} [width]
 * @property {number} [height]
 * @property {Array<*>} [data]
 * @property {Object} [margin]
 * @property {string} [yAxis]
 * @property {(dataPoint: { Id: number, Date: string, DeltaIncrease: number, DeltaDecrease: number, Transparent: number, Actual: number | null, Status: number | null, Notes: string | null, ReporterName: string | null }) => void} [dateClick]
 * @property {(data: import('recharts').BarRectangleItem, index: number, event: React.MouseEvent<SVGPathElement>) => void} [barClick]
 * @property {number} [dpCount]
 */

/**
 * @param {WaterfallChartProps} props
 */
const WaterfallChart = props => {
  const { width, height, data, margin, yAxis, dateClick, barClick, dpCount } = props;

  const canClick = dateClick != null && dpCount >= 1;
  const canBarClick = barClick != null && dpCount >= 1;

  const getStyle = () => {
    if (canBarClick) {
      return { cursor: 'pointer' };
    }
  };

  return (
    <div onClick={e => e.stopPropagation()}>
      <BarChart
        width={width}
        height={height}
        data={data}
        margin={{
          top: margin.top,
          right: margin.right,
          left: margin.left,
          bottom: margin.bottom
        }}>
        <CartesianGrid strokeDasharray='3 3' />
        <XAxis
          dataKey='Id'
          tickFormatter={value => getDataPoint(value, data).Date}
          onClick={canClick ? element => dateClick(getDataPoint(element.value, data)) : undefined}>
          {getXAxisLabel('Data Collection Date')}
        </XAxis>
        <YAxis>{getYAxisLabel(yAxis)}</YAxis>
        {dpCount >= 1 ? (
          <Tooltip
            labelFormatter={value => getDataPoint(value, data)?.Date}
            itemSorter={itemSorterFn(LEGEND_ORDER)}
            formatter={(value, name) => {
              if (name === 'Transparent') return null;
              return [value, name];
            }}
          />
        ) : null}
        <Legend
          wrapperStyle={{ paddingTop: '1.5rem' }}
          align='center'
          itemSorter={itemSorterFn(LEGEND_ORDER)}
        />
        {dpCount >= 1 ? <Bar dataKey='Transparent' stackId='a' fill='Transparent' /> : null}
        {dpCount >= 1 ? (
          <Bar
            name='Delta Increase'
            dataKey='DeltaIncrease'
            stackId='a'
            fill='#0F69A0'
            style={getStyle()}
            onClick={canBarClick ? barClick : undefined}
          />
        ) : null}
        {dpCount >= 1 ? (
          <Bar
            name='Delta Decrease'
            dataKey='DeltaDecrease'
            stackId='a'
            fill='#e57524'
            style={getStyle()}
            onClick={canBarClick ? barClick : undefined}
          />
        ) : null}
      </BarChart>
    </div>
  );
};

const getDataPoint = (id, data) => data.find(x => x.Id === id);

export default WaterfallChart;
